import { FC, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Col, Divider, Input, Row, Typography } from 'antd';
import { TbArrowRight, TbInfoCircle, TbLockMinus } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { PlatformAlert } from '@/shared/components/alerts';
import { PlatformForm as Form } from '@/shared/components/forms';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useUserStore } from '@/stores';
import { SetStateMethod } from '@/types';

interface DisableTFAProps {
    setOpen: SetStateMethod<boolean>;
    setEnabled: SetStateMethod<boolean>;
    setVerified: SetStateMethod<boolean>;
}

const DisableTFA: FC<DisableTFAProps> = ({ setEnabled, setOpen, setVerified }): JSX.Element => {
    const { selectedUser: user, fetchUser, updateSelectedUserLocal } = useUserStore();
    const [otp, setOtp] = useState<string>('');
    const [disabling, setDisabling] = useState(false);

    const disableTFA = async () => {
        const normalizedOtp = otp.replace(/\D/g, '');

        if (disabling || normalizedOtp.length !== 6) {
            return;
        }

        try {
            setDisabling(true);

            const res = await authorizedAxiosInstance.post('/api/tfa/disable', { otp: normalizedOtp });

            if (res?.status === 200) {
                updateSelectedUserLocal({ otpEnabled: false, otpCode: null });

                setEnabled(false);
                setVerified(false);
                setOpen(false);

                toast('TFA Disabled', { icon: <TbLockMinus size='1rem' />, description: user?.emailAddress });

                await fetchUser();
            }
        } catch (error) {
            toast('Failed to disable TFA');

            captureException(error);
        } finally {
            setDisabling(false);
        }
    };

    return (
        <>
            <PlatformAlert showIcon status='success' message='Two-Factor Authentication is enabled' />
            <Divider style={{ marginBottom: '0.5rem' }} />
            <Typography.Title level={5}>Disable Two-Factor Authentication</Typography.Title>
            <PlatformAlert
                title='Disable two-factor authentication'
                showIcon
                icon={<TbInfoCircle />}
                status='info'
                description={
                    <span>
                        You can disable two-factor authentication by re-entering the one-time passcode (OTP) from your
                        authentication app below. This means you will not have to enter in a one-time passcode each time
                        you log in to Countercyclical.
                    </span>
                }
                style={{
                    marginBottom: '0.75rem',
                    marginTop: '0.5rem',
                }}
            />
            <Divider style={{ marginBottom: '0.5rem' }} />
            <Form layout='vertical'>
                <Row gutter={[16, 0]}>
                    <Col flex='auto'>
                        <Form.Item label='One-Time Passcode (OTP)' style={{ marginBottom: 0 }}>
                            <Input
                                maxLength={6}
                                placeholder='Input Six-Digit Code...'
                                style={{ width: '100%' }}
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                            />
                        </Form.Item>
                    </Col>
                    <Col flex='none'>
                        <Form.Item label={<></>} style={{ marginBottom: 0 }}>
                            <Button
                                type='dashed'
                                onClick={disableTFA}
                                loading={disabling}
                                disabled={otp.length !== 6}
                                danger
                            >
                                Verify & Disable
                                <TbArrowRight
                                    size='1rem'
                                    style={{ verticalAlign: '-0.175rem', marginLeft: '0.375rem' }}
                                />
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </>
    );
};

export default DisableTFA;
